import {
  collectionDetailsFragment,
  collectionFragment,
  collectionProductFragment
} from "@saleor/fragments/collections";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeQuery from "@saleor/hooks/makeQuery";
import makeTopLevelSearch from "@saleor/hooks/makeTopLevelSearch";
import gql from "graphql-tag";

import { TypedQuery } from "../queries";
import { BrandsList, BrandsListVariables } from "./types/BrandsList";
import {
  CollectionDetails,
  CollectionDetailsVariables
} from "./types/CollectionDetails";
import {
  CollectionList,
  CollectionListVariables
} from "./types/CollectionList";
import {
  TenantStoreList,
  TenantStoreListVariables
} from "./types/TenantStoreList";

export const collectionList = gql`
  ${collectionFragment}
  query CollectionList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: CollectionFilterInput
    $sort: CollectionSortingInput
  ) {
    collections(
      first: $first
      after: $after
      before: $before
      last: $last
      filter: $filter
      sortBy: $sort
    ) {
      edges {
        node {
          ...CollectionFragment
          products {
            totalCount
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`;
export const useCollectionListQuery = makeQuery<
  CollectionList,
  CollectionListVariables
>(collectionList);

export const collectionDetails = gql`
  ${collectionDetailsFragment}
  ${collectionProductFragment}
  query CollectionDetails(
    $id: ID!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    collection(id: $id) {
      ...CollectionDetailsFragment
      deeplinkUrl
      shortDynamicLink
      products(first: $first, after: $after, before: $before, last: $last) {
        edges {
          node {
            ...CollectionProductFragment
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
    shop {
      homepageCollection {
        id
      }
    }
  }
`;
export const TypedCollectionDetailsQuery = TypedQuery<
  CollectionDetails,
  CollectionDetailsVariables
>(collectionDetails);

const getBrandsList = gql`
  ${pageInfoFragment}
  query BrandsList(
    $first: Int!
    $last: Int
    $before: String
    $after: String
    $query: String!
    $status: SmopStatusEnum
    $malls: [ID]
  ) {
    search: brandsPrivate(
      first: $first
      last: $last
      before: $before
      after: $after
      filter: { search: $query, malls: $malls }
      status: $status
    ) {
      edges {
        node {
          id
          name
          code
          legalName
          storeType
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const useBrandQuery = makeTopLevelSearch<
  BrandsList,
  BrandsListVariables
>(getBrandsList);

const getStoreList = gql`
  ${pageInfoFragment}
  query TenantStoreList(
    $first: Int!
    $last: Int
    $before: String
    $after: String
    $status: SmopStatusEnum
    $query: String!
    $isAllowedForNationwideDiscovery: Boolean
  ) {
    search: tenantsPrivate(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: {
        search: $query
        isAllowedForNationwideDiscovery: $isAllowedForNationwideDiscovery
      }
      status: $status
    ) {
      edges {
        node {
          id
          storeId
          status
          brand {
            id
            name
            legalName
          }
          mall {
            id
            name
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
  }
`;

export const useStoreListQuery = makeTopLevelSearch<
  TenantStoreList,
  TenantStoreListVariables
>(getStoreList);
